import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Keyboard from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'
import useIdleTimer from '../hooks/useIdleTimer'

import pattern from '../assets/images/pattern.png'

import Modal from '../components/Modal'
import Button from '../components/Button'

const keyboardLayouts = {
  'default-en': [
    '1 2 3 4 5 6 7 8 9 0 - {bksp}',
    'q w e r t y u i o p',
    'a s d f g h j k l',
    '{shift} z x c v b n m , . {shift}',
    '.com @ {space} .ca',
  ],
  'shift-en': [
    '! @ # $ ^ & * ( ) _ + {bksp}',
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    '{shift} Z X C V B N M ? . {shift}',
    '.com @ {space} .ca',
  ],
  'default-fr': [
    '1 2 3 4 5 6 7 8 9 0 - {bksp}',
    'q w e r t y u i o p ç .',
    'a s d f g h j k l ; è à',
    '{shift} ù z x c v b n m , é {shift}',
    '.com @ {space} .ca',
  ],
  'shift-fr': [
    '! @ # $ % & * ( ) _ + {bksp}',
    "Q W E R T Y U I O P Ç '",
    'A S D F G H J K L È À "',
    '{shift} Ù Z X C V B N M , É {shift}',
    '.com @ {space} .ca',
  ],
  numbers: ['1 2 3', '4 5 6', '7 8 9', '0 {bksp}'],
}

const DataEntry = () => {
  const { t, i18n } = useTranslation()

  const [keyboardIns, setKeyboardIns] = useState(null)
  const [focusedInput, setFocusedInput] = useState('')
  const [maxLength, setMaxLength] = useState(99)
  const [keyLayoutName, setKeyLayoutName] = useState('default-' + i18n.language)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
  })
  const [isChecked, setChecked] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    setKeyLayoutName('default-' + i18n.language)
  }, [i18n.language])

  const onKeyboardInit = (keyboard) => {
    setKeyboardIns(keyboard)
  }

  const setValueToInput = (value) => {
    const updated = { ...formData }

    switch (focusedInput) {
      case 'firstName':
      case 'lastName':
        updated[focusedInput] = value ? value[0].toUpperCase() + value.slice(1) : ''
        break
      default:
        updated[focusedInput] = value
        break
    }

    setFormData((prevState) => ({
      ...prevState,
      ...updated,
    }))
  }

  const onChange = (value) => {
    setValueToInput(value)
  }

  const onKeyPress = (button) => {
    if (button === '{shift}' || button === '{lock}') {
      handleShift()
    } else if (button === '{numbers}' || button === '{abc}') {
      handleNumbers()
    }

    let currentInputValue = ''
    switch (focusedInput) {
      case 'firstName':
        currentInputValue = formData.firstName
        break
      default:
        break
    }

    let specialValue = null
    switch (button) {
      case '{@}':
        specialValue = currentInputValue + '@'
        break
      case '{.}':
        specialValue = currentInputValue + '.'
        break
      case '{-}':
        specialValue = currentInputValue + '-'
        break
      case '{_}':
        specialValue = currentInputValue + '_'
        break
      case '{.com}':
        specialValue = currentInputValue + '.com'
        break
      case '{#}':
        specialValue = currentInputValue + '#'
        break
      case '{+}':
        specialValue = currentInputValue + '+'
        break
      default:
        break
    }

    if (specialValue) {
      setValueToInput(specialValue)
    }
  }

  const handleShift = () => {
    const newLayoutName = keyLayoutName.startsWith('default') ? 'shift-' + i18n.language : 'default-' + i18n.language

    setKeyLayoutName(newLayoutName)
  }

  const handleNumbers = () => {
    const currentLayout = keyLayoutName
    const numbersToggle = currentLayout !== 'numbers' ? 'numbers' : 'default'

    setKeyLayoutName(numbersToggle)
  }

  const onInputFocus = (inputName, input) => {
    const inputValue = input.value
    const maxLength = Number(input.getAttribute('maxlength')) || 99
    const type = input.getAttribute('type')

    if (keyboardIns !== null) {
      keyboardIns.setInput(inputValue)
    }

    setMaxLength(maxLength)
    setKeyLayoutName(type === 'number' ? 'numbers' : 'default-' + i18n.language)
    setFocusedInput(inputName)
  }

  const closeModal = () => setModalOpen(false)

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault()

      const { firstName, lastName } = formData

      if (!firstName || !lastName) return

      localStorage.setItem('user-info', JSON.stringify(formData))

      navigate('/quiz')
    },
    [formData, navigate]
  )

  useIdleTimer()

  return (
    <>
      <form
        className="_gradient-border text-white w-[80%] h-[60%] px-[4rem] pt-[10rem] mx-auto mt-[8rem] relative z-30"
        onSubmit={onSubmit}
      >
        <div className="grid grid-cols-12 gap-x-12 gap-y-4">
          <div className="flex flex-col gap-2 col-span-12">
            <label className="text-4xl mb-2">{t('firstName')}</label>
            <input
              value={formData.firstName}
              onFocus={(e) => onInputFocus('firstName', e.target)}
              onChange={(e) => setValueToInput(e.target.value)}
              type="text"
              className="text-[1.8rem] text-black py-4 px-4 rounded-lg focus:outline-neutral-800 shadow-[inset_-5.663px_-4.114px_3px_rgba(255,255,255,0.04),inset_5.516px_4.31px_3px_rgba(0,0,0,0.09)]"
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
            />
          </div>
          <div className="flex flex-col gap-2 col-span-12">
            <label className="text-4xl mb-2">{t('lastName')}</label>
            <input
              value={formData.lastName}
              onFocus={(e) => onInputFocus('lastName', e.target)}
              onChange={(e) => setValueToInput(e.target.value)}
              type="text"
              className="text-[1.8rem] text-black py-4 px-4 rounded-lg focus:outline-neutral-800 shadow-[inset_-5.663px_-4.114px_3px_rgba(255,255,255,0.04),inset_5.516px_4.31px_3px_rgba(0,0,0,0.09)]"
              autoComplete="off"
              spellCheck={false}
              required
            />
          </div>
          <div className="mt-8 relative flex items-center col-span-12">
            <input type="checkbox" checked={isChecked} required onChange={(e) => {}} className="w-0 h-0" />
            <div
              className="w-[60px] h-[60px] border-gray-200 border-[3px] rounded-2xl relative z-10 cursor-pointer"
              onClick={() => (!isChecked ? setChecked(true) : setChecked(false))}
            >
              <div
                className={`bg-white w-[30px] h-[30px] rounded absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] ${
                  isChecked ? 'scale-1' : 'scale-0'
                }`}
              ></div>
            </div>
            <div className="w-[60px] h-[60px] absolute left-0 z-20" onClick={() => setModalOpen(true)}></div>
            <div className="text-[2rem] text-left ml-8 cursor-pointer" onClick={() => setModalOpen(true)}>
              {t('agreement1')} <span className="underline">{t('agreement2')}</span>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="text-2xl font-bold min-h-[360px]">
            <Keyboard
              layoutName={keyLayoutName}
              onChange={onChange}
              disableButtonHold={true}
              onKeyPress={onKeyPress}
              onInit={onKeyboardInit}
              stopMouseUpPropagation={true}
              stopMouseDownPropagation={true}
              layout={keyboardLayouts}
              maxLength={maxLength}
              display={{
                '{bksp}': '⌫',
                '{shift}': '⇧',
                '{controlleft}': 'ctrl ⌃',
                '{controlright}': 'ctrl ⌃',
                '{@}': '@',
                '{-}': '-',
                '{_}': '_',
                '{.}': '.',
                '{.com}': '.com',
                '{space}': ' ',
                '{return}': 'return',
              }}
            />
          </div>
        </div>

        <Button type="submit">{t('submit')}</Button>

        <Modal isModalOpen={isModalOpen} closeModal={closeModal} setIsChecked={setChecked} />
      </form>

      <img src={pattern} alt="pattern" className="lg:w-full w-[60%] absolute bottom-0 right-0" />
    </>
  )
}

export default DataEntry
